import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserAuditLogs, AUDIT_ACTIONS, RESOURCE_TYPES } from '../../services/auditService';

const AuditLogViewer = () => {
  const { userProfile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');
  const [maxResults, setMaxResults] = useState(100);

  useEffect(() => {
    loadLogs();
  }, [userProfile, maxResults]);

  const loadLogs = async () => {
    if (!userProfile?.practiceId) return;
    try {
      setLoading(true);
      setError('');
      const data = await getUserAuditLogs(userProfile.practiceId, maxResults);
      setLogs(data);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError('Failed to load audit logs. Make sure the required Firestore index exists.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) return date.toDate().toLocaleString();
    return new Date(date).toLocaleString();
  };

  const getActionBadge = (action) => {
    const styles = {
      [AUDIT_ACTIONS.CREATE]: 'bg-green-100 text-green-800',
      [AUDIT_ACTIONS.READ]: 'bg-blue-100 text-blue-800',
      [AUDIT_ACTIONS.UPDATE]: 'bg-yellow-100 text-yellow-800',
      [AUDIT_ACTIONS.DELETE]: 'bg-red-100 text-red-800',
      [AUDIT_ACTIONS.UPLOAD]: 'bg-purple-100 text-purple-800',
      [AUDIT_ACTIONS.DOWNLOAD]: 'bg-indigo-100 text-indigo-800',
      [AUDIT_ACTIONS.LOGIN]: 'bg-cyan-100 text-cyan-800',
      [AUDIT_ACTIONS.LOGOUT]: 'bg-gray-100 text-gray-800',
      [AUDIT_ACTIONS.ACCESS_DENIED]: 'bg-red-200 text-red-900',
    };
    return styles[action] || 'bg-gray-100 text-gray-800';
  };

  const getActionIcon = (action) => {
    const icons = {
      [AUDIT_ACTIONS.CREATE]: 'M12 4v16m8-8H4',
      [AUDIT_ACTIONS.READ]: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
      [AUDIT_ACTIONS.UPDATE]: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
      [AUDIT_ACTIONS.DELETE]: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
      [AUDIT_ACTIONS.LOGIN]: 'M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1',
      [AUDIT_ACTIONS.LOGOUT]: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
    };
    return icons[action] || 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
  };

  const filteredLogs = logs.filter(log => {
    if (filterAction && log.action !== filterAction) return false;
    if (filterResource && log.resourceType !== filterResource) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500 mt-1">
            HIPAA-compliant activity monitoring and access tracking
          </p>
        </div>
        <button
          onClick={loadLogs}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Action Type</label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Actions</option>
              {Object.values(AUDIT_ACTIONS).map(action => (
                <option key={action} value={action}>{action.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Resource Type</label>
            <select
              value={filterResource}
              onChange={(e) => setFilterResource(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Resources</option>
              {Object.values(RESOURCE_TYPES).map(type => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Show</label>
            <select
              value={maxResults}
              onChange={(e) => setMaxResults(parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="50">Last 50</option>
              <option value="100">Last 100</option>
              <option value="250">Last 250</option>
              <option value="500">Last 500</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Events', count: logs.length, color: 'gray' },
          { label: 'Logins', count: logs.filter(l => l.action === 'login').length, color: 'cyan' },
          { label: 'Data Access', count: logs.filter(l => l.action === 'read').length, color: 'blue' },
          { label: 'Modifications', count: logs.filter(l => ['create', 'update', 'delete'].includes(l.action)).length, color: 'yellow' },
          { label: 'Access Denied', count: logs.filter(l => l.action === 'access_denied').length, color: 'red' },
        ].map(stat => (
          <div key={stat.label} className="bg-white shadow rounded-lg p-3 text-center">
            <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.count}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Log Timeline */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Showing {filteredLogs.length} of {logs.length} events
          </p>
        </div>
        <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
          {filteredLogs.length > 0 ? (
            filteredLogs.map((log) => (
              <div key={log.id} className="px-6 py-3 hover:bg-gray-50 flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${getActionBadge(log.action)}`}>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getActionIcon(log.action)} />
                    </svg>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getActionBadge(log.action)}`}>
                      {log.action?.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-400">
                      {log.resourceType}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5">
                    <span className="font-medium">User:</span> {log.userId?.substring(0, 8)}...
                    {' '}
                    <span className="font-medium">Resource:</span> {log.resourceId?.substring(0, 8)}...
                  </p>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {JSON.stringify(log.details)}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-gray-500">{formatDate(log.timestamp)}</p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[150px]">
                    {log.userAgent?.includes('Chrome') ? 'Chrome' :
                     log.userAgent?.includes('Firefox') ? 'Firefox' :
                     log.userAgent?.includes('Safari') ? 'Safari' : 'Unknown'}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>No audit logs found matching filters</p>
            </div>
          )}
        </div>
      </div>

      {/* HIPAA Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800">HIPAA Compliance</h4>
            <p className="text-xs text-blue-600 mt-1">
              All access to Protected Health Information (PHI) is automatically logged. Audit logs are immutable and retained per HIPAA requirements. These logs track who accessed what data, when, and from where.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;
